import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { Auth } from './auth';
import { BillingService } from '../billing.service';

/**
 * Redirects a hard-locked org (overdue invoice — the only account-wide
 * lock; see billing.hardlock.BillingHardLockPermission) to /suspended
 * BEFORE the dashboard shell and its resolvers run, so no workspace data
 * is fetched or rendered client-side while the org is locked.
 * Server-side enforcement (the 402 itself) is the real access control —
 * this guard is the UX for it.
 *
 * card_required (a write against a live project with no card — see
 * billing.hardlock.CardRequiredForLiveProjectWrites) is deliberately NOT
 * checked here: it never locks an org out of navigating anywhere, only
 * out of specific writes, and is handled inline wherever it's hit (see
 * CardRequiredService / auth-interceptor.ts).
 *
 * /dashboard/admin/billing is exempt: a locked billing admin must still
 * reach the billing page to pay / add a card (its APIs are allowlisted
 * server-side).
 */
export const billingLockGuard: CanActivateFn = async (_route, state) => {
  const auth = inject(Auth);
  const billing = inject(BillingService);
  const router = inject(Router);

  if (!auth.isLoggedIn()) return true; // authGuard handles the redirect
  if ((state.url || '').startsWith('/dashboard/admin/billing')) return true;

  // A 402 already seen this session settles it without another request.
  if (billing.hardLocked()) return router.createUrlTree(['/suspended']);

  try {
    const status = await billing.getBillingStatusCached();
    if (status.billing_hard_locked) {
      return router.createUrlTree(['/suspended']);
    }
  } catch {
    // Status unavailable: let the navigation through — every org-scoped API
    // call is enforced server-side, so nothing sensitive can load anyway.
  }
  return true;
};
